/**
 * @fileoverview Parent-portal auth context (F1).
 *
 * Mirrors OrgAuthContext: bootstrap from `/parent/auth/me`, expose the
 * account, its runtime `flags` (Spec 19's calendar sync) and refresh/logout. The one difference is that a 403 is treated as
 * "signed out" rather than an error — the API raises exactly one, and it means
 * the address has not been verified yet, which from the shell's point of view
 * is indistinguishable from having no session.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import parentApi from "@/lib/parentApi";

export const ParentAuthContext = createContext({ parent: null, loading: true });

export function ParentAuthProvider({ children }) {
  const [parent, setParent] = useState(null);
  const [flags, setFlags] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await parentApi.get("/parent/auth/me");
      setParent(data.parent);
      setFlags(data.flags || null);
    } catch {
      setParent(null);
      setFlags(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await parentApi.post("/parent/auth/logout");
    } finally {
      setParent(null);
      window.location.href = "/parent/login";
    }
  }, []);

  const value = useMemo(
    () => ({ parent, flags, loading, refresh, logout, setParent }),
    [parent, flags, loading, refresh, logout],
  );

  return <ParentAuthContext.Provider value={value}>{children}</ParentAuthContext.Provider>;
}

export function useParentAuth() {
  return useContext(ParentAuthContext);
}
